import { useLocation, useNavigate } from "react-router-dom";
import { Avatar } from "../components/AccountMenu";
import BackLink from "../components/BackLink";
import ConfirmDelete from "../components/ConfirmDelete";
import MutationError from "../components/MutationError";
import LanguageSwitch from "../components/LanguageSwitch";
import LegalLinks from "../components/LegalLinks";
import ReadingSettings from "../components/ReadingSettings";
import { useT } from "../i18n";
import { useReaderSettings } from "../player/readerSettings";
import { useDeleteAccount, useSession } from "../session";

/**
 * Settings is two sections: **Profile** — who you are and the language the
 * interface speaks — and **Reading** — how a chapter looks.
 *
 * The split is the point (requirement 0033). They had been one list, and that
 * said the interface language was a reading setting like the text size. It is
 * not: the language belongs to the account and follows the reader between
 * devices (ADR-0023), while size, face and theme describe a screen and a room
 * and stay on the device, so a phone in bed can be dark and large while a
 * laptop at a desk is not.
 *
 * Signed in or not, everyone gets this page — the language and the reading
 * settings work for a visitor too; the profile section then holds the language
 * alone, because there is nobody to name.
 *
 * While it is open the chosen theme, size and face are applied to the whole
 * page, the same way the reader applies them, so choosing Sepia shows sepia
 * rather than a swatch of it.
 */
export default function Settings() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, update] = useReaderSettings();
  const { me } = useSession();

  // Back to wherever settings were opened from — usually the middle of a
  // chapter. A settings page reached from a shared link has nowhere to go
  // back to, so it goes to the library.
  const back = () => (location.key !== "default" ? navigate(-1) : navigate("/"));

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink onClick={back} testID="settings-back">
        {t.settings.back}
      </BackLink>
      <h1 className="mt-4 mb-8 text-[1.75rem] leading-tight font-semibold tracking-tight">
        {t.settings.title}
      </h1>

      <section className="mb-10" data-testid="settings-profile">
        <h2 className="text-sm font-medium text-ink-soft">{t.settings.profile}</h2>
        <p className="mt-1 mb-4 text-xs text-muted">{t.settings.profileNote}</p>
        {me && (
          <div className="mb-4 flex items-center gap-3">
            <Avatar name={me.user.name || me.user.email} imageURL={me.user.image_url} size={40} />
            <div className="min-w-0">
              {me.user.name && (
                <p dir="auto" className="truncate text-sm font-medium">
                  {me.user.name}
                </p>
              )}
              <p data-testid="settings-email" className="truncate text-xs text-muted">
                {me.user.email}
              </p>
            </div>
          </div>
        )}
        <h3 className="mb-3 text-xs font-medium text-muted">{t.common.language}</h3>
        <LanguageSwitch />
      </section>

      <section>
        <h2 className="text-sm font-medium text-ink-soft">{t.settings.reading}</h2>
        <p className="mt-1 mb-4 text-xs text-muted">{t.settings.readingNote}</p>
        <ReadingSettings settings={settings} onChange={update} />
      </section>

      {me && <DeleteAccount onDeleted={() => navigate("/", { replace: true })} />}

      <LegalLinks className="mt-12" />
    </main>
  );
}

/**
 * DeleteAccount is the one way out (requirement 0062). One confirmation, and
 * the question says it cannot be undone; the note above it says what goes and
 * what stays, so nobody finds out after.
 */
function DeleteAccount({ onDeleted }: { onDeleted: () => void }) {
  const t = useT();
  const remove = useDeleteAccount(onDeleted);
  return (
    <section className="mt-10 border-t border-line pt-8" data-testid="settings-account">
      <h2 className="text-sm font-medium text-ink-soft">{t.settings.account}</h2>
      <p className="mt-1 mb-4 text-xs text-muted">{t.settings.deleteNote}</p>
      <ConfirmDelete
        label={t.settings.deleteAccount}
        question={t.settings.deleteQuestion}
        testId="delete-account"
        disabled={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
      <MutationError error={remove.error} fallback={t.settings.deleteFailed} />
    </section>
  );
}
